import { isNum } from 'aegion_common_utilities/lib/format/numeric';

import { getCustomer } from '../../../../util/user';

/* ************************
 * HELPERS
 */

const isDefined = v => v !== undefined && v !== null;

const isEmptyString = v =>
	isDefined(v) && typeof v === 'string' && v.trim().length === 0;

const isValidJobNumber = jn => isDefined(jn) && isNum(jn);

const isValidItemId = sid =>
	isDefined(sid) && !isEmptyString(sid) && sid.split('-').length > 1;

const isValidLatitude = num =>
	isDefined(num) && isNum(num) && Number.isFinite(num) && Math.abs(num) <= 90;

const isValidLongitude = num =>
	isDefined(num) && isNum(num) && Number.isFinite(num) && Math.abs(num) <= 180;

const isValidZoomLevel = num =>
	isDefined(num) && isNum(num) && Number.isFinite(num) && num >= 1 && num <= 24;

/* ************************
 * SCANLINE
 */

// Line Details
export const generateScanlineLineDetailsLink = (lineExternalId, linename) => {
	let hasError = false;
	if (!isValidItemId(lineExternalId)) {
		console.error(
			`Error: generateScanlineLineDetailsLink invalid parameter lineExternalId: ${lineExternalId}`
		);
		hasError = true;
	}
	if (!isDefined(linename) || isEmptyString(linename)) {
		console.error(
			`Error: generateScanlineLineDetailsLink invalid parameter linename: ${linename}`
		);
		hasError = true;
	}

	if (hasError) {
		return '';
	}

	const queryString = [
		`line=${encodeURIComponent(lineExternalId)}`,
		`linename=${encodeURIComponent(linename)}`
	].join('&');

	return `/scanline/map?${queryString}`;
};

// Survey Details - opens line details page, additional parameters should result in different charts being open by default.
export const generateScanlineSurveyDetailsLink = (
	lineExternalId,
	linename,
	surveyExternalId,
	surveyType
) => {
	let hasError = false;
	if (!isValidItemId(lineExternalId)) {
		console.error(
			`Error: generateScanlineSurveyDetailsLink invalid parameter lineExternalId: ${lineExternalId}`
		);
		hasError = true;
	}
	if (!isDefined(linename) || isEmptyString(linename)) {
		console.error(
			`Error: generateScanlineSurveyDetailsLink invalid parameter linename: ${linename}`
		);
		hasError = true;
	}
	if (!isValidItemId(surveyExternalId)) {
		console.error(
			`Error: generateScanlineSurveyDetailsLink invalid parameter surveyExternalId: ${surveyExternalId}`
		);
		hasError = true;
	}
	if (!isDefined(surveyType) || isEmptyString(surveyType)) {
		console.error(
			`Error: generateScanlineSurveyDetailsLink invalid parameter surveyType: ${surveyType}}`
		);
		hasError = true;
	}

	if (hasError) {
		return '';
	}

	const queryString = [
		`line=${encodeURIComponent(lineExternalId)}`,
		`linename=${encodeURIComponent(linename)}`,
		`survey=${encodeURIComponent(surveyExternalId)}`,
		`surveyType=${encodeURIComponent(surveyType)}`
	].join('&');

	return `/scanline/map?${queryString}`;
};

/* ************************
 * FIELDLINE
 */

// Job Search
export const generateFieldlineJobSearchLink = jobNumber => {
	if (!isValidJobNumber(jobNumber)) {
		console.error(
			`Error: generateFieldlineJobSearchLink invalid parameter jobNumber: ${jobNumber}`
		);
		return '';
	}

	const queryString = `q=${encodeURIComponent(`aegion_job_id:${jobNumber}`)}`;
	return `/FieldLineReact?${queryString}`;
};

// Line Search
export const generateFieldlineLineSearchLink = linename => {
	if (!isDefined(linename) || isEmptyString(linename)) {
		console.error(
			`Error: generateFieldlineLineSearchLink invalid parameter linename: ${linename}`
		);
		return '';
	}

	const queryString = `q=${encodeURIComponent(`linename:${linename}`)}`;
	return `/FieldLineReact?${queryString}`;
};

/* ************************
 * CISVIEW
 */

// Job Search
export const generateCISViewJobSearchLink = jobNumber => {
	if (!isValidJobNumber(jobNumber)) {
		console.error(
			`Error: generateCISViewJobSearchLink invalid parameter jobNumber: ${jobNumber}`
		);
		return '';
	}

	const queryString = `job=${encodeURIComponent(jobNumber)}`;
	return `/CISLine?${queryString}`;
};

// Survey Details Link
export const generateCISViewSurveyLink = surveyExternalId => {
	if (!isValidItemId(surveyExternalId)) {
		console.error(
			`Error: generateCISViewSurveyLink invalid parameter surveyExternalId: ${surveyExternalId}`
		);
		return '';
	}

	return `/CISLine/Job/Show%20File/${encodeURIComponent(
		surveyExternalId
	)}?tabSize=1`;
};

/* ************************
 * ASSETVIEW
 */
const DEFAULT_AV_ZOOM_LEVEL = 15;

// Survey Details Link
export const generateAssetViewLink = ({
	zoomLevel = DEFAULT_AV_ZOOM_LEVEL,
	lat,
	lon,
	customer // optional
}) => {
	let hasError = false;
	if (!isValidZoomLevel(zoomLevel)) {
		console.error(
			`Error: generateAssetViewLink invalid parameter zoomLevel: ${zoomLevel}`
		);
		hasError = true;
	}
	if (!isValidLatitude(lat)) {
		console.error(`Error: generateAssetViewLink invalid parameter lat: ${lat}`);
		hasError = true;
	}
	if (!isValidLongitude(lon)) {
		console.error(`Error: generateAssetViewLink invalid parameter lon: ${lon}`);
		hasError = true;
	}

	if (hasError) {
		return '';
	}

	const loggedInCustomer = getCustomer();
	let customerQuery = '';
	if (
		isDefined(customer) &&
		!isEmptyString(customer) &&
		loggedInCustomer === 'Aegion'
	) {
		customerQuery = `&s=${encodeURIComponent(`{"Customers":["${customer}"]}`)}`;
	}

	return `/AssetView/${zoomLevel}/${lat}/${lon}/?tab=map${customerQuery}`;
};
